<template>
  <div class="quiz-card">
    <!-- Archived/never answered, or a malformed (e.g. tampered) payload -->
    <div v-if="!questionsValid || (!live && !storedAnswers && !finished)" class="quiz-ended">
      {{ $t("interactions.activityEnded") }}
    </div>

    <!-- Score card (live finish or archived-with-response) -->
    <div v-else-if="finished" class="quiz-score" role="status">
      <div class="quiz-score-title">{{ $t("interactions.scoreTitle") }}</div>
      <div class="quiz-score-stars" aria-hidden="true">
        <span v-for="starIndex in grade.total" :key="starIndex" class="star"
          :class="{ earned: starIndex <= grade.score }">★</span>
      </div>
      <div class="quiz-score-line">
        {{ $t("interactions.scoreLine", { score: grade.score, total: grade.total }) }}
      </div>
      <div v-if="bestStreak > 1" class="quiz-score-streak">
        {{ $t("interactions.bestStreak", { streak: bestStreak }) }}
      </div>
    </div>

    <!-- Active round -->
    <div v-else>
      <div v-if="interaction.title" class="quiz-title">{{ interaction.title }}</div>
      <div class="quiz-progress" aria-hidden="true">
        <span
          v-for="(question, index) in interaction.questions"
          :key="index"
          class="progress-dot"
          :class="dotClass(index)"
        ></span>
      </div>
      <div class="quiz-question">{{ currentQuestion.prompt }}</div>
      <div class="pill-group" role="group">
        <button
          v-for="(option, index) in currentQuestion.options"
          :key="index"
          type="button"
          class="pill-button"
          :class="optionClass(index)"
          :disabled="revealed"
          @click="answer(index)"
        >
          {{ option }}
        </button>
      </div>
      <div v-if="revealed" class="quiz-feedback" role="status">
        <div class="quiz-feedback-verdict" :class="{ good: lastCorrect }">
          {{ lastCorrect ? $t("interactions.correct") : $t("interactions.incorrect") }}
        </div>
        <div v-if="currentQuestion.explanation" class="quiz-feedback-explanation">
          {{ currentQuestion.explanation }}
        </div>
        <button type="button" class="quiz-next-button" @click="next">
          {{ isLastQuestion ? $t("interactions.finish") : $t("interactions.next") }}
        </button>
      </div>
      <button v-if="!revealed" type="button" class="quiz-skip" @click="endEarly">
        {{ $t("interactions.skip") }}
      </button>
    </div>
  </div>
</template>

<script>
import confetti from "canvas-confetti";
import { gradeQuiz } from "@/scripts/interactionUtils.js";
import { useHaptics } from "@/composables/useHaptics.js";

// Mirrors CONFETTI_PALETTE in ConfettiCanvas.vue (canvas can't read CSS
// custom properties); keep in sync with the --al-* tokens.
const CONFETTI_PALETTE = ["#2d90eb", "#eb882d", "#10b981", "#d7eafe"];

export default {
  name: "QuizCard",
  props: {
    interaction: { type: Object, required: true },
    live: { type: Boolean, default: false },
  },
  emits: ["submit"],
  data() {
    return {
      currentIndex: 0,
      answers: (Array.isArray(this.interaction.questions)
        ? this.interaction.questions
        : []
      ).map(() => null),
      revealed: false,
      lastCorrect: false,
      finishedLocally: false,
    };
  },
  computed: {
    // The questions payload is read from a client-writable RTDB node, so a
    // tampered or corrupted quiz must degrade to the ended state rather than
    // crash the render (a throw here white-screens the whole chat). Gating on
    // this keeps the score screen and active round off the malformed path, so
    // their per-question accesses stay safe.
    questionsValid() {
      const questions = this.interaction.questions;
      return (
        Array.isArray(questions) &&
        questions.length > 0 &&
        questions.every((question) => question && typeof question === "object")
      );
    },
    storedAnswers() {
      return this.interaction.response?.answers || null;
    },
    finished() {
      return this.finishedLocally || !!this.storedAnswers;
    },
    effectiveAnswers() {
      return this.storedAnswers || this.answers;
    },
    grade() {
      return gradeQuiz(this.interaction.questions, this.effectiveAnswers);
    },
    bestStreak() {
      let best = 0;
      let run = 0;
      this.interaction.questions.forEach((question, index) => {
        if (this.effectiveAnswers[index] === question.correctIndex) {
          run++;
          best = Math.max(best, run);
        } else {
          run = 0;
        }
      });
      return best;
    },
    currentQuestion() {
      return this.interaction.questions[this.currentIndex];
    },
    isLastQuestion() {
      return this.currentIndex === this.interaction.questions.length - 1;
    },
  },
  watch: {
    // A failed submit re-arms the card (live flips back to true) but leaves it
    // stuck on the local score screen. Drop back to the round so the learner
    // can re-finish and retry the submission.
    live(isLive) {
      if (isLive && !this.storedAnswers) {
        this.finishedLocally = false;
        // Clear the current question's answer too: otherwise revealed is false
        // (options enabled, Finish button hidden) while the progress dot still
        // reads as answered. Nulling it restores a clean re-answer state.
        this.answers[this.currentIndex] = null;
      }
    },
  },
  methods: {
    dotClass(index) {
      const answer = this.answers[index];
      if (answer === null) return { pending: index === this.currentIndex };
      return {
        good: answer === this.interaction.questions[index].correctIndex,
        bad: answer !== this.interaction.questions[index].correctIndex,
      };
    },
    optionClass(index) {
      if (!this.revealed) return {};
      const { correctIndex } = this.currentQuestion;
      const chosen = this.answers[this.currentIndex] === index;
      return {
        correct: index === correctIndex,
        incorrect: chosen && index !== correctIndex,
      };
    },
    answer(index) {
      if (this.revealed || !this.live) return;
      this.answers[this.currentIndex] = index;
      this.lastCorrect = index === this.currentQuestion.correctIndex;
      this.revealed = true;
      useHaptics().impact(this.lastCorrect ? "Light" : "Medium");
    },
    next() {
      this.revealed = false;
      if (this.isLastQuestion) {
        this.finish(false);
      } else {
        this.currentIndex++;
      }
    },
    endEarly() {
      this.finish(true);
    },
    finish(skipped) {
      this.finishedLocally = true;
      const perfect = this.grade.score === this.grade.total && !skipped;
      if (perfect) {
        this.launchConfetti();
        useHaptics().notification("SUCCESS");
      }
      this.$emit("submit", {
        type: "quiz",
        answers: [...this.answers],
        skipped,
      });
    },
    launchConfetti() {
      const prefersReducedMotion =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (prefersReducedMotion) return;
      confetti({
        particleCount: 120,
        spread: 75,
        origin: { y: 0.7 },
        colors: CONFETTI_PALETTE,
        disableForReducedMotion: true,
      });
    },
  },
};
</script>

<style scoped>
.quiz-card {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--al-border);
}

.quiz-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.quiz-progress {
  display: flex;
  gap: 0.35rem;
  margin-bottom: 0.75rem;
}

.progress-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: color-mix(in srgb, var(--al-text) 20%, transparent);
}

.progress-dot.pending {
  background-color: var(--al-primary);
}

.progress-dot.good {
  background-color: var(--al-success);
}

.progress-dot.bad {
  background-color: var(--al-danger);
}

.quiz-question {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.pill-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.pill-button {
  border: 1.5px solid var(--al-border);
  background: transparent;
  color: var(--al-text);
  border-radius: 999px;
  padding: 0.4rem 1rem;
  font-size: 0.95rem;
  cursor: pointer;
  transition: transform 0.12s ease, background-color 0.15s ease,
    border-color 0.15s ease;
}

.pill-button:not(:disabled):active {
  transform: scale(0.96);
}

.pill-button.correct {
  border-color: var(--al-success);
  background-color: color-mix(in srgb, var(--al-success) 15%, transparent);
}

.pill-button.incorrect {
  border-color: var(--al-danger);
  background-color: color-mix(in srgb, var(--al-danger) 12%, transparent);
}

.quiz-feedback {
  margin-top: 0.75rem;
}

.quiz-feedback-verdict {
  font-weight: 600;
  color: var(--al-danger);
}

.quiz-feedback-verdict.good {
  color: var(--al-success);
}

.quiz-feedback-explanation {
  margin-top: 0.25rem;
  font-size: 0.9rem;
}

.quiz-next-button {
  margin-top: 0.5rem;
  border: none;
  border-radius: 999px;
  padding: 0.4rem 1.25rem;
  background-color: var(--al-primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}

.quiz-skip {
  display: block;
  margin-top: 0.75rem;
  background: none;
  border: none;
  color: var(--al-text);
  opacity: 0.6;
  font-size: 0.8rem;
  cursor: pointer;
  text-decoration: underline;
}

.quiz-ended {
  font-size: 0.9rem;
  opacity: 0.7;
}

.quiz-score {
  text-align: center;
  padding: 0.5rem 0;
}

.quiz-score-title {
  font-weight: 700;
  font-size: 1.05rem;
}

.quiz-score-stars .star {
  font-size: 1.4rem;
  opacity: 0.25;
}

.quiz-score-stars .star.earned {
  opacity: 1;
  color: var(--al-orange);
}

.quiz-score-streak {
  font-size: 0.85rem;
  opacity: 0.8;
}
</style>
